import { useNavigate } from "@remix-run/react";
import { Button, Tabs, Tab, ScrollShadow } from "@heroui/react";
import { LuCheck } from "react-icons/lu";
import { ProductCard } from "app/components/Product/ProductCard";

import { useQuery } from "@tanstack/react-query";
import { fetchLatestProduct, fetchTrendingProductsApi } from "app/api/product/products.api";
import React from "react";
import { ProductObject } from "app/api/product/product.type";
import { camelCaseToTitleCase } from "app/utils/fn";
import { UserType } from "app/api/user/user.types";
import CardSkeleton from "./CardSkeleton";
import Carousel from "../Carousel";

interface FeaturedProductsProps {
  gridNo: number;
  userType?: UserType;
  loadTrending: boolean;
}

type TabType = "trendingProducts" | "latestProducts";

const TAB_CONFIG = {
  trendingProducts: {
    title: "Trending Products",
    queryKey: "trendingProducts",
    fetchFn: fetchTrendingProductsApi,
    getData: (data: any) => data?.docs || [],
  },
  latestProducts: {
    title: "Latest Products",
    queryKey: "latestProducts",
    fetchFn: fetchLatestProduct,
    getData: (data: any) => data || [],
  },
} as const;

const FeaturedProducts = ({ userType, loadTrending }: FeaturedProductsProps) => {
  const navigate = useNavigate();
  const [selected, setSelected] = React.useState<TabType>("trendingProducts");

  const currentTab = TAB_CONFIG[selected];

  const { data, isLoading } = useQuery({
    queryKey: [currentTab.queryKey, userType],
    queryFn: () =>
      currentTab.fetchFn({
        userType: userType || UserType.Non_VIP,
        ...(selected === "trendingProducts" ? { limit: 20 } : {}),
      }),
    refetchOnMount: false,
    enabled: selected === "trendingProducts" ? loadTrending : true,
    staleTime: Infinity,
  });

  const handleSeeMore = () => {
    navigate("/search?sort=recently added");
  };

const renderProductCard = (item: ProductObject) => (
  <ProductCard
    key={item?._id}
    labels={item?.labels || []}
    image={item?.overview?.heroImage}
    images={item?.product?.images}
    title={item?.overview?.name}
    productCode={item?.overview?.code}
    description={item?.product?.description}
    userTypePrice={item?.userTypePrice}
    qty={item?.quantity}
    slug={item?.slug}
    category={item?.category?.name || item?.product?.categorisation?.productType?.typeName}
    subCatgory={item?.subCategory?.name || item?.product?.categorisation?.supplierSubCategory}
    isSpecialSupplier={item?.supplier?.isSpecialSupplier || false}
  />
);

  const renderLoadingSkeleton = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );

  const renderProducts = () => {
    const products = currentTab.getData(data);

    if (selected === "trendingProducts") {
      return (
        <>
          <ScrollShadow className="flex md:hidden gap-2" hideScrollBar orientation="horizontal">
            {products.map((item: ProductObject) => (
              <div key={item._id} className="min-w-52">
                {renderProductCard(item)}
              </div>
            ))}
          </ScrollShadow>
          <div className="hidden md:block">
            <Carousel numberOfItems={4} loop={true}>
              {products.map((item: ProductObject) => renderProductCard(item))}
            </Carousel>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4">
          {products.map((item: ProductObject) => renderProductCard(item))}
        </div>
        <div className="mx-auto my-8 w-fit">
          <Button color="primary" variant="flat" onPress={handleSeeMore}>
            View More
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="px-3 md:px-6 lg:px-8 xl:px-12 min-h-[30rem]">
      <div className="flex flex-col gap-4 relative container mx-auto">
        <div className="flex flex-col lg:flex-row md:flex-row justify-between items-center border-b border-gray md:p-5 !pb-0 pt-1 gap-4">
          <div className="flex space-x-3 items-center justify-center">
            <LuCheck size={25} className="text-primary" />
            <span className="text-forground text-2xl font-semibold text-center">
              {camelCaseToTitleCase(selected)}
            </span>
          </div>

          <div className="flex md:w-7/12 flex-wrap md:flex-nowrap items-start justify-end md:gap-6 gap-3 px-2">
            <div className="">
              <Tabs
                aria-label="Product Filter Options"
                color="primary"
                variant="underlined"
                defaultSelectedKey={"trendingProducts"}
                onSelectionChange={(key: any) => setSelected(key)}
                classNames={{
                  tabList: "gap-6 w-full relative rounded-none p-0",
                  cursor: "w-full bg-black",
                  tab: "max-w-fit px-0 h-12",
                  tabContent: "group-data-[selected=true]:text-primary",
                }}
              >
                {Object.entries(TAB_CONFIG).map(([key, config]) => (
                  <Tab
                    key={key}
                    title={
                      <div
                        aria-label={config.title}
                        className="flex items-center text-base font-medium space-x-2 cursor-pointer"
                      >
                        <span>{config.title}</span>
                      </div>
                    }
                  />
                ))}
              </Tabs>
            </div>
          </div>
        </div>

        {isLoading ? renderLoadingSkeleton() : renderProducts()}
      </div>
    </div>
  );
};

export default FeaturedProducts;
